import React, { createContext, useCallback, useMemo, useState } from "react";

import { computeAffineTransform, computeImageCorners, computeResidualError } from "./affineTransform";
import { GCP, GeoReferencerContextValue, GeoReferencerState, GeoRefMode } from "./types";

const initialState: GeoReferencerState = {
  imageName: null,
  imageUrl: null,
  imageWidth: 0,
  imageHeight: 0,
  gcps: [],
  activeGcpId: null,
  mode: "idle",
  opacity: 0.7,
  isVisible: true,
  transformMatrix: null,
};

export const GeoReferencerContext = createContext<GeoReferencerContextValue | null>(null);

/**
 * Derive the next GCP number from existing labels to avoid gaps/duplicates.
 */
function getNextGcpNumber(gcps: GCP[]): number {
  const usedNumbers = gcps.map((gcp) => {
    const match = gcp.label.match(/^GCP (\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = usedNumbers.length > 0 ? Math.max(...usedNumbers) : 0;
  return max + 1;
}

export const GeoReferencerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<GeoReferencerState>(initialState);

  const loadImage = useCallback((file: File) => {
    // Reset all state immediately so stale data doesn't linger during async loading
    setState(initialState);

    // Strip the file extension to get a default image name
    const imageName = file.name.replace(/\.[^.]+$/, "");

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        setState({
          ...initialState,
          imageName,
          imageUrl: dataUrl,
          imageWidth: img.naturalWidth,
          imageHeight: img.naturalHeight,
        });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }, []);

  const addGcp = useCallback(() => {
    setState((prev) => {
      const id = `gcp-${Date.now()}`;
      const label = `GCP ${getNextGcpNumber(prev.gcps)}`;
      const newGcp: GCP = { id, imageX: 0, imageY: 0, mapLng: null, mapLat: null, label };
      return {
        ...prev,
        gcps: [...prev.gcps, newGcp],
        activeGcpId: id,
        mode: "place-image" as GeoRefMode,
      };
    });
  }, []);

  const removeGcp = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      gcps: prev.gcps.filter((gcp) => gcp.id !== id),
      activeGcpId: prev.activeGcpId === id ? null : prev.activeGcpId,
      mode: prev.activeGcpId === id ? "idle" : prev.mode,
    }));
  }, []);

  const setImagePoint = useCallback((gcpId: string, x: number, y: number) => {
    setState((prev) => ({
      ...prev,
      gcps: prev.gcps.map((gcp) => (gcp.id === gcpId ? { ...gcp, imageX: x, imageY: y } : gcp)),
      mode: "place-map",
    }));
  }, []);

  const setMapPoint = useCallback((gcpId: string, lng: number, lat: number) => {
    setState((prev) => ({
      ...prev,
      gcps: prev.gcps.map((gcp) => (gcp.id === gcpId ? { ...gcp, mapLng: lng, mapLat: lat } : gcp)),
      activeGcpId: null,
      mode: "idle",
    }));
  }, []);

  const startPlacingImage = useCallback((gcpId: string) => {
    setState((prev) => ({ ...prev, activeGcpId: gcpId, mode: "place-image" }));
  }, []);

  const startPlacingMap = useCallback((gcpId: string) => {
    setState((prev) => ({ ...prev, activeGcpId: gcpId, mode: "place-map" }));
  }, []);

  const cancelPlacing = useCallback(() => {
    setState((prev) => ({ ...prev, activeGcpId: null, mode: "idle" }));
  }, []);

  const setOpacity = useCallback((opacity: number) => {
    setState((prev) => ({ ...prev, opacity }));
  }, []);

  const setVisible = useCallback((isVisible: boolean) => {
    setState((prev) => ({ ...prev, isVisible }));
  }, []);

  const clearAll = useCallback(() => {
    setState((prev) => ({ ...prev, gcps: [], activeGcpId: null, mode: "idle" }));
  }, []);

  // Compute the affine transform whenever GCPs change
  const completeGcps = useMemo(
    () => state.gcps.filter((gcp): gcp is GCP & { mapLng: number; mapLat: number } => gcp.mapLng != null && gcp.mapLat != null),
    [state.gcps]
  );

  const completeGcpCount = completeGcps.length;

  const transformMatrix = useMemo(() => {
    if (completeGcps.length < 3) return null;
    return computeAffineTransform(
      completeGcps.map((gcp) => ({
        imageX: gcp.imageX,
        imageY: gcp.imageY,
        mapLng: gcp.mapLng,
        mapLat: gcp.mapLat,
      }))
    );
  }, [completeGcps]);

  const imageCorners = useMemo(() => {
    if (!transformMatrix || !state.imageWidth || !state.imageHeight) return null;
    return computeImageCorners(transformMatrix, state.imageWidth, state.imageHeight);
  }, [transformMatrix, state.imageWidth, state.imageHeight]);

  const residualError = useMemo(() => {
    if (!transformMatrix || completeGcps.length === 0) return null;
    return computeResidualError(
      transformMatrix,
      completeGcps.map((gcp) => ({
        imageX: gcp.imageX,
        imageY: gcp.imageY,
        mapLng: gcp.mapLng,
        mapLat: gcp.mapLat,
      }))
    );
  }, [transformMatrix, completeGcps]);

  const value: GeoReferencerContextValue = useMemo(
    () => ({
      ...state,
      transformMatrix,
      imageCorners,
      completeGcpCount,
      residualError,
      loadImage,
      addGcp,
      removeGcp,
      setImagePoint,
      setMapPoint,
      startPlacingImage,
      startPlacingMap,
      cancelPlacing,
      setOpacity,
      setVisible,
      clearAll,
    }),
    [
      state,
      transformMatrix,
      imageCorners,
      completeGcpCount,
      residualError,
      loadImage,
      addGcp,
      removeGcp,
      setImagePoint,
      setMapPoint,
      startPlacingImage,
      startPlacingMap,
      cancelPlacing,
      setOpacity,
      setVisible,
      clearAll,
    ]
  );

  return <GeoReferencerContext.Provider value={value}>{children}</GeoReferencerContext.Provider>;
};
